import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Radar, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Empty, PageHeader, StatusBadge } from "@/components/common";
import { VehicleTypeIcon } from "@/components/VehicleRow";
import { VehicleFormDialog } from "@/components/admin/VehicleFormDialog";
import { useFleet } from "@/context/FleetContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { VEHICLE_TYPES, timeAgo, vehicleStatus , isGpsFresh } from "@/lib/fleet";

export default function Vehicles() {
  const { vehicles, reload } = useFleet();
  const { isAdmin, isSeller, isSuperAdmin } = useAuth();
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [adding, setAdding] = useState(false);
  const [del, setDel] = useState(null);

  const list = vehicles.filter((v) => {
    const s = q.toLowerCase();
    return (!s || `${v.name} ${v.plate} ${v.imei} ${v.customer_name || ""}`.toLowerCase().includes(s)) && (type === "all" || v.type === type);
  });

  const remove = async () => {
    try {
      await api.delete(`/admin/vehicles/${del.id}`);
      toast.success("Kendaraan dihapus");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };

  return (
    <div>
      <PageHeader eyebrow="Armada" title="Kendaraan" subtitle={`${vehicles.length} kendaraan terhubung dengan GPS tracker (IMEI).`}
        actions={(isAdmin || isSeller) && <button onClick={() => setAdding(true)} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground" data-testid="vehicle-add-btn"><Plus className="h-4 w-4" /> Tambah Kendaraan</button>} />
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama, plat, IMEI, customer" className="gt-input pl-9" data-testid="vehicles-search" />
        </div>
        <div className="gt-noscroll flex gap-1.5 overflow-x-auto">
          {[["all", "Semua"], ...Object.entries(VEHICLE_TYPES)].map(([k, l]) => (
            <button key={k} onClick={() => setType(k)} data-testid={`vehicles-type-${k}`}
              className={`shrink-0 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${type === k ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="vehicles-grid">
        {list.map((v, i) => {
          const st = vehicleStatus(v);
          return (
            <div key={v.id} className="gt-card gt-rise group p-4 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-primary/40" style={{ animationDelay: `${i * 30}ms` }} data-testid={`vehicle-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent"><VehicleTypeIcon type={v.type} className="h-5 w-5" /></div>
                <StatusBadge status={st} />
              </div>
              <div className="mt-3 text-base font-bold">{v.name}</div>
              <div className="font-mono text-xs text-muted-foreground">{v.plate} · IMEI {v.imei}</div>
              {isAdmin && <div className="mt-1 text-xs text-muted-foreground">{v.customer_name}</div>}
              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border pt-3 text-center">
                <div><div className="gt-eyebrow text-[9px]">Kecepatan</div><div className="font-mono text-sm font-bold">{isGpsFresh(v) ? Math.round(v.speed || 0) : "—"}</div></div>
                <div><div className="gt-eyebrow text-[9px]">ACC</div><div className={`font-mono text-sm font-bold ${v.acc ? "text-emerald-500" : "text-slate-400"}`}>{v.acc ? "ON" : "OFF"}</div></div>
                <div><div className="gt-eyebrow text-[9px]">Update</div><div className="text-xs font-semibold">{timeAgo(v.server_time)}</div></div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Link to={`/vehicles/${v.id}`} className="flex-1 rounded-xl bg-foreground py-2 text-center text-xs font-bold text-background hover:opacity-90" data-testid={`vehicle-detail-btn-${v.id}`}>Detail</Link>
                <Link to={`/live?v=${v.id}`} className="flex h-8 w-9 items-center justify-center rounded-xl border border-border hover:border-primary" data-testid={`vehicle-live-btn-${v.id}`}><Radar className="h-4 w-4" /></Link>
                {isSuperAdmin && <button onClick={() => setDel(v)} className="flex h-8 w-9 items-center justify-center rounded-xl border border-border text-red-500 hover:border-red-500" data-testid={`vehicle-delete-btn-${v.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
              <div className="mt-2 text-[10.5px] text-muted-foreground">Masa aktif s/d <span className="font-mono">{v.expires_at || "-"}</span></div>
            </div>
          );
        })}
      </div>
      {!list.length && <div className="gt-card"><Empty title="Tidak ada kendaraan" /></div>}
      {(isAdmin || isSeller) && <VehicleFormDialog open={adding} onOpenChange={setAdding} onSaved={reload} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-border bg-popover">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus kendaraan?</AlertDialogTitle>
            <AlertDialogDescription>{del?.name} ({del?.plate}) akan dihapus. Perangkat IMEI {del?.imei} dilepas dan dapat dipasang ke kendaraan lain. Data histori GPS tetap tersimpan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-700" data-testid="vehicle-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
